import { fn, INamed, IInfOperation } from '../utils';
import {
    NavPageItem, IPageQueryParameters,
    Observable, INavPageClientInfo, TNavUrlAccess, ResolveNavUrl
} from '../navdata';
import {
    Pages, IFDRW_WFInstance, IFDRW_Workflow_Instance_Action_List,
    IFDRW_Workflow_User_Instances_List, IFDRW_WF_Action_Fwd_Comments_List,
    IFDRW_Workflow_Step_List, SelectedActionType
} from './rwfpages.generated';
import { LogReauthenticate, RouteWorkflowInstance, ValidateSignature, ReAuthenticate, CloseWorkflowInstance, ExecutePostingCheck } from './rwf.functions';
import { CompatibilityService } from '../services/compatibilityservice';
import { IObservableArray } from '../nav';
import { AsObservableArray, ReplaceObservableData, constants } from '../navkendo';
import Filter = fn.Filter;
import checkPromise = fn.checkPromise;
import { FDRW_WFInstance } from '@common/rwf/rwf.const';
import { RwfApplicationService } from '@common/rwf/rwf.application';
import { IRwfBootstrapParameters } from './rwf.bootsrap';

/** Source for Routing : Either an Instance Item, UserInstance List Item or Workflowid  */
export type TRouteInstanceSource = NavPageItem<IFDRW_WFInstance> | string | IFDRW_Workflow_User_Instances_List;

export const ExternalRefresh = "ExternalRefresh";
const Default_SubActionTitle = "Unteraktion wählen";

/** Aggregated Info for Routing Models */
export class RouteInstanceModels extends Observable {
    constructor(
        public readonly $c: CompatibilityService,
        public readonly Instances: RouteInstanceModel[],
        public readonly UserInstancesClient: INavPageClientInfo<IFDRW_Workflow_User_Instances_List>,
        public readonly RwfBootstrap: IRwfBootstrapParameters
    ) {
        super();
        this.Rwf = $c.resolve("Rwf")("RwfApplication");

        this.one(constants.NavDestroy, this.Rwf.RegisterEditChange((changes) => {
            // filter for items we show, exlude 'Open' Action (wait for close ...)
            var matches = changes.map(c => Instances.filter(item => item.ID == c.InstanceId && c.Action != "Open")[0]).filter(fn.isSet);
            matches.filter(item => !item.IsDone && !item.IsRouting && !item.Disabled);
            if (matches.length > 0) {
                // ReValidate Instances , finally recalculate
                const afterRefresh = () => this.trigger(ExternalRefresh);
                $c.all(matches.map(m => m.RefreshInstance())).then(afterRefresh, afterRefresh);
            }
        }));
    }

    public destroy() {
        this.trigger(constants.NavDestroy);
        this.Instances.forEach(item => item.destroy());
    }
    public readonly Rwf: RwfApplicationService;

    /** 'Mass' Routing Operation has been Cancelled */
    Cancelled?: boolean;
    /** 'Mass' Close Operation is Pending */
    ClosePending: boolean;

    /** Currently Running Mass routing Operation */
    StartedOperation: PromiseLike<any>;

    /** Container for "ReAuthenticated" Instances */
    ReAuthenticated = new Array<RouteInstanceModel>();

    /** Check Signatures are Valid on Items needing Signature, if not Show Modal Signing Window */
    CheckSignMandatoryValid(selectedInstances: RouteInstanceModel[], cbOnSignError?: (m: RouteInstanceModel) => void): PromiseLike<any> {
        const $c = this.$c;
        const navUrl = this.UserInstancesClient.NavUrl;
        var signData = selectedInstances.filter(item => item.SelectedAction && item.SelectedAction.SignatureMandatory);
        var signIds = signData.map(item => item.ID);
        if (signIds.length == 0)
            return $c.when();

        const toValidate = signData.map(item => ({
            ID: item.ID,
            Instance: item.Instance,
            SelectedAction: item.SelectedAction,
            CurrentStep: item.CurrentStep,
            Name: item.UserInstanceItem.Name || item.ID
        }));

        // Only Remove Erroneous Document on Sign Error
        var onSignError = (e: { ID: string, Instance: NavPageItem, Error?: string, Continue?: boolean }) => {
            var erroneous = selectedInstances.filter(s => s.ID == e.ID)[0];
            var idx = erroneous && selectedInstances.indexOf(erroneous);
            if (idx >= 0) {
                erroneous.RefreshState({
                    IsError: true, SignDocError: true,
                    Error: ("" + e.Error) || "Fehler bei der Signatur"
                });
                if (typeof cbOnSignError === "function")
                    cbOnSignError(erroneous);
                e.Continue = true;
            }
        };
        toValidate.forEach(t => t.Instance.Model.bind("RWFSignError", onSignError));
        return $c.finally(ValidateSignature($c, toValidate, navUrl), () => {
            toValidate.forEach(t => t.Instance.Model.unbind("RWFSignError", onSignError));
        });
    }

    // TODO Unifiy with massroute.ctrl
    /** Check  Mass Mandatory Actions (some get Validated per item
      - Reauthenticate
      - Signature
      */
    ValidateMandatoryActions(selectedInstances: RouteInstanceModel[]) {
        const $c = this.$c;
        var instances = selectedInstances;
        this.ReAuthenticated.length = 0;
        // Check for Forced Reauthentication
        var toReAuthInstances = instances.filter(item => item.SelectedAction && item.SelectedAction.Reauthenticate);
        return $c.when(toReAuthInstances.length > 0 &&
            ReAuthenticate($c).then(() => { this.ReAuthenticated = toReAuthInstances; }))
            .then(c => this.CheckSignMandatoryValid(selectedInstances)).then(fn.ToFunc(instances));
    }
}

/** Resolved Info Item for (Mass) Routing (KWF3) */
export class RouteInstanceModel extends Observable implements IRWFRoutingState {
    /** Load Routing Models for  */
    public static LoadRoutingInfo($c: CompatibilityService, rSources: TRouteInstanceSource[], navUrl: TNavUrlAccess) {
        const NavClient = $c.resolve("NavClient");
        const RwfBootstrap = $c.resolve("Rwf")("RwfBootstrap");
        return RwfBootstrap.Resolve.then(() => $c.all({
            NavUrl: ResolveNavUrl(navUrl, true),
            ItemsSource: ResolveItemsSource($c, rSources, navUrl),
            rwfBootstrap: RwfBootstrap.Resolve
        })).then(s => {
            const rSources = s.ItemsSource;
            const navUrl = s.NavUrl.FullUrl;

            let ids = rSources.map(item => item.ID),
                resolvedSteps: INamed<IFDRW_Workflow_Step_List>,

                /** Prepare Info Per Item (IKWFMassRouteInfo) after Forced Action Resolution */
                fillInstances = LoadActions($c, ids, navUrl, { explicitFill: true, groupItems: true }).then(actionGroups => {
                    var allRootActions = fn.FlattenArray(actionGroups.map(g => g.RootItems));
                    var allRealActions = fn.FlattenArray(actionGroups.map(g => g.Items));
                    var rootActions = fn.GroupBy(allRootActions, action => normalizeId(action.WFInstanceNo));
                    var realActions = fn.GroupBy(allRealActions, action => normalizeId(action.WFInstanceNo));
                    // Load Instances
                    var result = rSources.map(item => {
                        const id = normalizeId(item.ID);
                        return new RouteInstanceModel(item.Instance, item.Item, rootActions[id] || [], realActions[id] || []);
                    });
                    /** Attach Current Step Info */
                    var usedStepIds = fn.distinct(result.map(item => item.Instance && item.Instance.GetValue("CurrentWorkflowStep", true)).filter(fn.isSet));
                    var attachStepInfos = NavClient.ReadMultiple<"FDRW_Workflow_Step_List">({
                        PageName: "FDRW_Workflow_Step_List",
                        Constraints: [{ FieldName: "Code", Value: usedStepIds.join("|") }],
                        PageCount: 0 // readAll
                    }, navUrl, true).then(stepResult => {
                        resolvedSteps = fn.ToDictionary(stepResult.Data, "Code");
                        // attach current step, Current/Default Action
                        result.forEach(r => r.CurrentStep = resolvedSteps[r.Model.CurrentWorkflowStep]);
                        result.filter(r => r.CurrentStep?.BatchProcessingStepDisabled === true && !r.IsError).forEach(r => {
                            r.RefreshState({ Disabled: true, IsError: true, Error: "Diese Aktion darf nicht im Massenmodus ausgeführt werden!",});  
                        });
                        // attach Default/Stored Action Action
                        result.forEach(r => r.SelectedAction = ResolveInitialAction(r.Instance.Model, r.AllActions, r.CurrentStep.DefaultAction));
                        // Load Forwarding Comments
                        return NavClient.ReadMultiple<"FDRW_WF_Action_Fwd_Comments_List">({
                            PageName: "FDRW_WF_Action_Fwd_Comments_List", PageCount: 0,
                            Constraints: [{ FieldName: "WorkflowStep", Value: usedStepIds.join("|") }]
                        }, navUrl, true).then(commentsResult => {
                            var comments = commentsResult.Data;
                            var cmtsPerStepInfo = fn.GroupBy(comments, c => c.WorkflowStep);

                            result.forEach(r => {
                                r.AvailableComments = AsObservableArray(cmtsPerStepInfo[r.CurrentStep && r.CurrentStep.Code] || []);
                                const initFwdComment = r.Model.FWCommentCode;
                                r.CurrentAvailableComments = AsObservableArray(
                                    (r.SelectedAction && r.AvailableComments.filter(item => item.WorkflowAction == r.SelectedAction.WFActionCode)) || []);
                                if (initFwdComment) {
                                    r.SelectedComment = r.AvailableComments.filter(item => item.WorkflowForwardingComment === initFwdComment)[0];
                                }

                                r.CurrentSubActions = <any>new kendo.data.ObservableArray([]);
                                if (r.SelectedAction) {
                                    r.SelectRoutingAction(r.SelectedAction);
                                }
                            });
                        });
                    });
                    return attachStepInfos.then(() => result);
                });
            return $c.all({
                Instances: fillInstances,
                Client: NavClient.GetPageClientInfo<IFDRW_Workflow_User_Instances_List>(
                    Pages.FDRW_Workflow_User_Instances_List.PageName, navUrl)
            }).then(info => {
                var result = new RouteInstanceModels(
                    $c,
                    info.Instances,
                    info.Client,
                    s.rwfBootstrap);
                info.Instances.forEach(item => {
                    item.Parent = result;
                    item.refreshModel();
                });
                return result;
            });
        });
    }

    private constructor(
        /** the Loaded  Workflow Instance */
        public readonly Instance: NavPageItem<IFDRW_WFInstance>,
        /** The related data of MyInvoice */
        public readonly UserInstanceItem: IFDRW_Workflow_User_Instances_List,
        /** Top Level Actions */
        public readonly TopActions: IKWFRoutingAction[],
        // All Real Actions
        public readonly AllActions: IKWFRoutingAction[]) {
        super();

        Instance.Anchor.InstanceID = this.ID = Instance.GetValue("ID", true);
        this.Model = Instance.Model;

        this.Remark = Instance.GetValue("Remark", true);
        this.one(constants.NavDestroy, Instance.RegisterPropertyChanged("Remark", () => {
            this.Remark = Instance.GetValue("Remark", true);
        }));

        this.CheckinAction = {
            Enabled: false,
            Execute: () => CloseWorkflowInstance(Instance, { IsZwischenSpeichern: false })
        };
        this.CheckoutAction = {
            Enabled: false,
            Execute: () => Instance.ExecuteMethod(Pages.FDRW_WFInstance.Methods.Open)
        };
        Instance.RegisterPropertyChanged("CheckedOutBy", () => this.refreshModel());
    }

    public destroy() {
        this.trigger(constants.NavDestroy);
    }

    /** Root Anchor for Routing Models */
    Parent: RouteInstanceModels;

    /** the Workflow Id*/
    readonly ID: string;
    /** the Underlyiny Instance Data (= Instance.Model) */
    readonly Model: IFDRW_WFInstance;

    /** Current Step Info of the Item */
    CurrentStep: IFDRW_Workflow_Step_List;

    /** Currently Selected 'real' Action (initialized with Optional Default Value on Step) */
    SelectedAction: IKWFRoutingAction;

    /** Selected 'Root' Action in Hierarchical Display () */
    public SelectedRootAction: IKWFRoutingAction;
    /** Selected 'Sub' Action in Hierarchical Display */
    public SelectedSubAction: IKWFRoutingAction;
    /** Current Available Sub Actions in Hierarchical Display */
    public CurrentSubActions: IObservableArray<IKWFRoutingAction>;

    /** ALL Comments on Current Step (Caution: Validity only per selected Action ==> CurrentAvailableComments) */
    AvailableComments: IObservableArray<IFDRW_WF_Action_Fwd_Comments_List>;
    /** Available Comments for User in Current State */
    CurrentAvailableComments: IObservableArray<IFDRW_WF_Action_Fwd_Comments_List>;
    /** Currently Selected Comment */
    SelectedComment: IFDRW_WF_Action_Fwd_Comments_List;

    /** (Initial) Remark for Instance*/
    Remark: string;

    IsCheckedOut: boolean = false;

    IsCheckedOutByMe: boolean = false;

    public readonly CheckinAction: IInfOperation;

    public readonly CheckoutAction: IInfOperation;

    SubActionTitle: string = Default_SubActionTitle;
    /** Called from UI on Root Routing Action Change */
    SelectRoutingAction(action: IKWFRoutingAction) {
        const current = this;
        if (!action) {
            current.SelectedAction = current.SelectedSubAction = current.SelectedRootAction = undefined;
        }
        else if (action._GroupItem) {
            current.SelectedSubAction = action;
            current.SelectedRootAction = <IKWFRoutingAction>action._GroupItem;
            current.SelectedAction = action;
        } else {
            current.SelectedRootAction = action;
            current.SelectedSubAction = undefined;
            if (action._Items) {
                if (action._Items.length == 1) {
                    // PreSelect Single Sub Item ...
                    current.SelectedAction = current.SelectedSubAction = action._Items[0];
                } else {
                    current.SelectedAction = undefined;
                }
            }
            else
                current.SelectedAction = action;

            this.SubActionTitle = GetCurrentSubActionTitle(this.SelectedRootAction);
        }

        const currentSubActions = current.CurrentSubActions;
        current.SelectedAction = (current.SelectedAction && current.AllActions.filter(c => compareAction(c, current.SelectedAction))[0]) || undefined;
        var currActionCode = current.SelectedAction && current.SelectedAction.WFActionCode;
        current.SelectedRootAction = (current.SelectedRootAction && current.TopActions.filter(c => compareAction(c, current.SelectedRootAction))[0]) || undefined;
        ReplaceObservableData<IKWFRoutingAction>(currentSubActions,
            <any>(current.SelectedRootAction && current.SelectedRootAction._Items || []), actionId, (a, b) => { return actionId(a) == actionId(b); });

        // Adjust Available Comments from Current
        const CurrentAvailableComments = current.CurrentAvailableComments;

        ReplaceObservableData(CurrentAvailableComments, current.AvailableComments.filter(item => item.WorkflowAction === currActionCode), item => item.Key);
        if (current.SelectedComment) {
            // Validate Comment:
            current.SelectedComment = CurrentAvailableComments.filter(item => item.Key == current.SelectedComment.Key)[0]
                || CurrentAvailableComments.filter(item => item.Comment === current.SelectedComment.Comment)[0];
        }
        current.SelectedSubAction = (current.SelectedSubAction && currentSubActions.filter(c => compareAction(c, current.SelectedSubAction))[0]) ||
            // Imp. Select single subAction ...
            (!current.SelectedSubAction && currentSubActions.length == 1 && currentSubActions[0]) || undefined;
    }

    private refreshModel() {
        const Model = this.Model;
        const isDeleted = this.Instance && this.Instance.IsDeletedInNav;
        if (isDeleted) {
            this.CheckinAction.Enabled = this.CheckoutAction.Enabled = false;
            return;
        }
        const RwfBootstrap = this.Parent && this.Parent.RwfBootstrap;
        const isCheckedOut = this.IsCheckedOut = !!(Model && !!Model.CheckedOutBy);
        this.CheckinAction.Enabled = this.IsCheckedOutByMe =
            !!(isCheckedOut && RwfBootstrap && RwfBootstrap.RwfUser.ID == Model.CheckedOutBy);
        this.CheckoutAction.Enabled = !isCheckedOut;
        this.trigger("refreshModel");
    }

    /** Erfolgreich Weitergeleitet */
    public IsDone?: boolean;

    public IsRouting?: boolean;

    public IsError?: boolean;

    public Error?: string;

    public Disabled?: boolean;

    /**  Currently Running Instance Refresh Operation */
    _RefreshInstance: PromiseLike<any>;

    /** Build or Get Current Promise to Close the Instance (or nullOp if not Open) */
    Close: () => PromiseLike<any>;

    /** Start Routing this Instance */
    public Route(validateMandatoryActions: boolean = false) {
        if (validateMandatoryActions === true) {
            return this.Parent.ValidateMandatoryActions([this]).then(s => this.Route(false));
        }
        const Parent = this.Parent;
        const $c = Parent.$c;
        const WFInstance = Pages.FDRW_WFInstance;
        const wf = this.Instance;

        if (this.IsDone)
            return; // Do Not Start new Routing Op if already done
        if (this.IsRouting)
            return $c.reject(this.Error = "Weiterleitung bereits gestartet!");

        if (Parent.Cancelled) {
            this.RefreshState({ IsRouting: false });
            return $c.when(undefined);
        }
        var selRealAction = this.SelectedAction;
        if (!selRealAction)
            return $c.reject(this.Error = "Keine Aktion gewählt");
    
        if(this.CurrentStep.BatchProcessingStepDisabled === true) {
            return $c.reject(this.Error = "Diese Aktion darf nicht im Massenmodus ausgeführt werden!");
        }

        // Mittel prüfen notwendig ....
        if (selRealAction.CheckFunds == true) {
            return $c.reject("Für die gewählte Aktion ist ein Mittelprüfung erforderlich, Weiterleiten deshalb nur aus dem Einzelformular möglich!");
        }

        var preRoute = this._RefreshInstance || $c.when();

        // always open before any operations (might need CheckedOutBy Information)
        preRoute = preRoute.then(
            () => wf.ExecuteMethod(WFInstance.Methods.Open)).then(
                () => this.RefreshState({ IsOpen: true }));

        // CheckOrderOverrrun
        if (!!wf.ClientInfo.AllMethods[WFInstance.Methods.CheckOrderOverrun.MethodName]) {
            preRoute = preRoute.then(() => {
                return wf.ExecuteMethod(WFInstance.Methods.CheckOrderOverrun).then(r => {
                    var isOverrun = r && !r["Result"];
                    if (isOverrun === true)
                        throw new Error("Der Restbetrag für Bestellzeile(n) ist überschritten, Weiterleiten deshalb nur aus dem Einzelformular möglich! ");
                });
            });
        }

        // Reserved Fundings, Do not Show Message in Mass Route Scenario
        if (selRealAction.ReserveFunds) {
            preRoute = preRoute.then(() => wf.ExecuteMethod(WFInstance.Methods.ReserveFunds));
        }

        // if(selRealAction.LeaveWizardRequired) {
        // Wird in NAV ("Route") behandelt, ...
        // }

        if (selRealAction.PostingCheckRequired) {
            preRoute = preRoute.then(() => ExecutePostingCheck({
                $c : $c,
                WFInstance : wf,
                supressSucessError: true
            }));
        }
        var result = preRoute.then(() => {
            if (Parent.ReAuthenticated.indexOf(this) >= 0) {
                var logReAuthenticate = LogReauthenticate(wf);
            }
            return $c.when(logReAuthenticate).then(() => RouteWorkflowInstance(wf, {
                Action: selRealAction,
                Comment: this.SelectedComment,
                Remark: this.Remark
            }).then(r => {

            }));
        });
        this.RefreshState({ IsRouting: true, Error: "" });
        const doClose = () => this.Close && this.Close();
        result.then(() => {
            this.RefreshState({ IsDone: true, IsRouting: false });
            // Notify other Clients about routed Instance
            Parent.Rwf.NotifyEditChange([{ InstanceId: this.ID, Action: "Routed" }], this.Instance.ClientInfo.NavUrl);

            // Do Not Remove one by one at the Moment! (see instanceData : angular - grid)
        }, err => this.RefreshState({ IsRouting: false, Error: err }))
            .then(doClose, doClose);
        return result;
    }

    RefreshState(opt?: IRWFRoutingState) {
        if (opt) {
            var routeKeys: (keyof IRWFRoutingState)[] = ["IsDone", "IsRouting", "IsOpen", "IsError", "IsRefresh", "SignDocError"];
            routeKeys.forEach(prop => {
                if (opt[prop] != undefined) {
                    this[prop] = opt[prop];
                }
            });

            if ("Error" in opt) {
                this.Error = fn.error.IsCancel(opt.Error) ? "Abbruch durch Benutzer" : fn.resolveToError(opt.Error);
            }
        };
        // Refresh Disabled from new state
        this.Disabled = this.IsError || this.IsDone || this.IsRouting || !!this.Parent.StartedOperation;

    }

    /** Refresh Instance Information (e.g. after Item Got Changed from External) */
    RefreshInstance() {
        const $c = this.Parent.$c;
        if (this.IsRouting)
            return $c.reject("Operation bereits gestartet");

        if (this._RefreshInstance)
            return this._RefreshInstance;

        this.RefreshState({ IsRefresh: true });
        var prevStep = this.Model.CurrentWorkflowStep;

        const afterRefresh = () => {
            this._RefreshInstance = undefined;
            this.RefreshState({ IsRefresh: false });
        };
        return this._RefreshInstance = this.Instance.RevertItem(true).then(item => {
            var newStep = this.Model.CurrentWorkflowStep;
            if (newStep != prevStep && !this.Error) {
                this.RefreshState({ IsError: true, Error: "Das Formular wurde bereits weitergeleitet!" });
            }
        }, err => {
        }).then(afterRefresh, afterRefresh);
    }
}

/** normalize guid to text for matching  */
function normalizeId(guid: string) {
    return (guid || "").replace(/[\-\{\}]/g, "").toLowerCase();
}

function ResolveItemsSource($c: CompatibilityService, items: TRouteInstanceSource[], navUrl: TNavUrlAccess) {
    const sources = items.map(ResolveItemSource);

    const NavClient = () => $c.resolve("NavClient");
    const toResolveItems = sources.filter(item => item.ID && !item.Item);
    if (toResolveItems.length) {
        var r1 = NavClient().ReadMultiple<"FDRW_Workflow_User_Instances_List">({
            PageName: "FDRW_Workflow_User_Instances_List",
            Constraints: [{ FieldName: "ID", Value: toResolveItems.map(item => item.ID).join('|') }],
            PageCount: 0,
        }, navUrl).then(d => {
            const uItems = fn.ToDictionary(d.Data, item => normalizeId(item.ID));
            toResolveItems.forEach(item => {
                if (!(item.Item = uItems[normalizeId(item.ID)])) {
                    throw new Error(`Beleg '${item.ID}' konnte nicht geladen werden. Evt. wurde der Beleg bereits weitergeleitet.`);
                }
            });
        });
    }
    const toResolveInstances = sources.filter(item => item.ID && !item.Instance);
    if (toResolveInstances.length > 0) {
        const ids = toResolveInstances.map(item => item.ID),
            queryItems: IPageQueryParameters<IFDRW_WFInstance> = {
                PageName: Pages.FDRW_WFInstance.PageName,
                Constraints: [{ FieldName: "ID", Value: ids.join("|") }],
                PageCount: 0
            };
        var r2 = NavClient().LoadItemsSeperate<IFDRW_WFInstance>(queryItems, navUrl).then((data) => {
            const uItems = fn.ToDictionary(data, item => normalizeId(item.Model.ID));
            toResolveInstances.forEach(item => {
                if (!(item.Instance = uItems[normalizeId(item.ID)])) {
                    throw new Error(`Worfklow '${item.ID}' konnte nicht geladen werden. Evt. wurde der Worfklow bereits weitergeleitet.`);
                }
            });
        });
    }

    return $c.all([r1, r2]).then(() => sources);
}
function ResolveItemSource(item: TRouteInstanceSource) {
    if (typeof item === "string") {
        var source = {
            ID: item,
            Item: <IFDRW_Workflow_User_Instances_List>undefined,
            Instance: <NavPageItem<IFDRW_WFInstance>>undefined
        };
    }

    else if (!(item instanceof NavPageItem)) {
        if (item && item.ID && typeof item.ID === "string") {
            source = {
                ID: item.ID,
                Item: item,
                Instance: undefined
            };
        }
    } else {
        switch (item.PageName) {
            case FDRW_WFInstance:
                const instanceItem = <NavPageItem<IFDRW_WFInstance>>item;
                source = {
                    ID: instanceItem.Model.ID,
                    Instance: instanceItem,
                    Item: undefined
                };
                break;
            default:
                throw new Error(`Invalid Page "${item.PageName}"`);

        }
    }

    if (!source) {
        throw new Error('Failed to Resolve Workflow Instance Source!');
    }
    return source;
}

/** Load Actions of Workflow Instance(s), enrich with Calculated Title, Grouping Information */
export function LoadActions(this: void, $c: CompatibilityService, wfInstanceIds: string[], navUrl: string, options: { /** to be removed after 19.0.0 */ explicitFill?: boolean, groupItems?: boolean }) {
    options = $.extend({ explicitFill: false, groupItems: true }, options);
    if (options.explicitFill) {
        var fill = $c.resolve("Rwf")("RwfBootstrap").Resolve.then(rwf => rwf.RwfSettings.CheckPages.FillInstanceActions &&
            checkPromise("Das Befüllen der Workflowaktionen ist fehlgeschlagen", $c.post("/infoma.Web.KWF3/FillInstanceActions?"
                + fn.EncodeQueryData({ navUrl: navUrl }), wfInstanceIds).then(fn.noop)));
    }
    else {
        fill = $c.when(<any>undefined);
    }

    return fill.then(() => checkPromise("Das Laden der Workflowaktionen ist fehlgeschlagen", $c.resolve("NavClient").ReadMultiple<"FDRW_Workflow_Instance_Action_List">({
        PageName: "FDRW_Workflow_Instance_Action_List",
        Constraints: [{ FieldName: "WFInstanceNo", Value: wfInstanceIds.join('|') }], PageCount: 0
    }, navUrl, true).then(r => {
        var allActions = (<IKWFRoutingAction[]>(r.Data || [])).map(item => {
            var title = item.WFActionName || item.WFActionCode,
                userInfo = item.WFUserName || item.WFUserID;
            switch (item.WFActionType) {
                // NO: Problem : Explicit Single Item and Implicit not discernable
                // case SelectedActionType.Forward_to_workflow_part:
                //     var title = title + " - " + (item.WFActionNextPartName || item.WFActionNextPartCode);
                //     break;
                case SelectedActionType.Back_in_workflow_history:
                    var title = title + (item.WFActionNextStepName ? " - " + item.WFActionNextStepName : "");
                    if (userInfo)
                        title += " [" + userInfo + "]";
                    break;
                default:
                    var title = title + (userInfo ? " - " + userInfo : "");
                    break;
            }
            item.ID = item.No.toString();
            item.Title = title;
            // Attack "_IsBackAction" from Attached Actions' Type
            if (item.WFActionType && ActionTypesBack.indexOf(item.WFActionType) >= 0)
                item._IsBackAction = true;
            return item;
        });

        // Prepare Grouping Information
        var counter = -1;
        var perInstanceActions = fn.GroupBy(allActions, action => action.WFInstanceNo);
        function createGroupClone(items: IKWFRoutingAction[]) {
            var clone = <IKWFRoutingAction>JSON.parse(JSON.stringify(items[0]));
            clone.ID = (clone.No = --counter).toString();
            clone.Key = "";
            clone.ActionLocked = items.every(action => action.ActionLocked);
            clone._Items = items;
            return clone;
        }
        return fn.DictToArray(perInstanceActions).map(instanceActions => {
            const grouping = <IKWFRoutingActions>{ RootItems: [], Items: instanceActions, NoGroups: !options.groupItems };
            const rootItems = grouping.RootItems;
            const groupableActions = instanceActions.filter(item => options.groupItems &&
                (ActionTypesGroupable.indexOf(item.WFActionType) >= 0));
            const groupItems = fn.DictToArray(fn.GroupBy(groupableActions, action => action.WFActionCode)).map(items => {
                // Do not group when only one item
                if (items.length == 1) {
                    return items[0];
                }

                var clone = createGroupClone(items);
                clone.Title = clone.WFActionName || clone.WFActionCode;
                items.forEach(item => {
                    item._GroupItem = clone;
                    switch (item.WFActionType) {
                        case SelectedActionType.Forward_to_workflow_part:
                            item._GroupedTitle = item.WFActionNextPartName || item.WFActionNextPartCode;
                            break;
                        default:
                            item._GroupedTitle = item.WFUserName || item.WFUserID;
                            break;
                    }
                });
                return clone;
            });
            // Handle Back_in_workflow_history
            const backActions = instanceActions.filter(item => options.groupItems && (item.WFActionType == SelectedActionType.Back_in_workflow_history || item.WFActionType == SelectedActionType.Back_in_workflow_history_to_step));
            groupItems.push(...fn.DictToArray(fn.GroupBy(backActions, action => action.WFActionCode)).map(items => {
                var clone = createGroupClone(items);
                clone.Title = clone.WFActionName;
                items.forEach(item => {
                    var userId = item.WFUserName || item.WFUserID;
                    item._GroupItem = clone;
                    item._GroupedTitle = item.WFActionNextStepName + (userId ? " - " + userId : "");
                });
                return clone;
            }));

            // build Root Items : Items not attached to GroupItem + GroupItems, try to preserve order of items
            fn.ToArray(grouping.Items).forEach(item => {
                if (!item._GroupItem)
                    rootItems.push(item);
                else if (rootItems.indexOf(item._GroupItem) < 0)
                    rootItems.push(item._GroupItem);
            });

            //  TODO : ICON for PaymentOrder Item ? (others?) instead of adjusted Title
            var pmtOrderAction = allActions.filter(item => item.WFActionType == SelectedActionType.Back_in_workflow_history && item.PaymentOrderCreated == true)[0];
            if (pmtOrderAction) {
                var propName = options.groupItems ? "_GroupedTitle" : "Title";
                pmtOrderAction[propName] = "[Anordung]" + pmtOrderAction[propName];
            }

            return grouping;
        });
    })));
}

export function GetCurrentSubActionTitle(SelectedRootAction?: IFDRW_Workflow_Instance_Action_List) {
    const WFActionType = SelectedRootAction && SelectedRootAction.WFActionType;
    //const SubItems = (SelectedRootAction && SelectedRootAction._Items) || [];
    switch (WFActionType) {
        case SelectedActionType.Back_in_workflow_history:
        case SelectedActionType.Back_in_workflow_history_to_step:
        case SelectedActionType.Back_to_workflow_part:
            return "Schritt wählen";

        case SelectedActionType.Back_in_workflow_with_user_selection:
        case SelectedActionType.Back_to_step_with_user_selection:
        case SelectedActionType.Forward_to_step_with_user_selection:
            return "Benutzer wählen";
        default:
            return Default_SubActionTitle;
    }
}

/** Resolve Initial Action for a Workflow Instance (Either from Instance of from given default)  */
export function ResolveInitialAction(wf: IFDRW_WFInstance, actions: IKWFRoutingAction[], defaultActionId: string) {
    // Check for initial Action : Either from Item or Default
    defaultActionId = wf.SelectedAction || defaultActionId;
    if (!defaultActionId)
        return;

    var reduceIfAny = <T extends keyof IKWFRoutingAction>(key: T, value: IKWFRoutingAction[T]) => {
        var items = Filter(actions, key, value);
        actions = items && items.length ? items : actions;
    };
    // What about Duplicates!?!
    var actions = Filter(actions, "WFActionCode", defaultActionId);
    reduceIfAny("WFUserID", wf.DesiredNextUser);
    reduceIfAny("WFActionNextPartCode", wf.DesiredNextPart);
    reduceIfAny("WFActionNextStepCode", wf.DesiredNextStep);
    return actions[0];
}

/**  compare Actions, matching from Users Perspective **/
function compareAction(a1: IKWFRoutingAction, a2: IKWFRoutingAction) {
    if (!a1)
        return !a2;

    return actionId(a1) == actionId(a2);
}

/** Actions of different Instances are "identified" by title if possible (from users perspective, 2 * 'zurück' is considered same ...)
* TODO : Duplicate Titles per Action will break the system right now */
function actionId(a: IKWFRoutingAction) {
    if (!a)
        return "";

    // In case of "UserTitle" Title : take users perpective (for top actions only)
    var title = (a._GroupItem && (a._GroupedTitle + a._GroupItem.Title)) || a.Title;
    if (title)
        return a.WFActionType + ":" + title;
    // fallback : technical keys ...
    return (a.WFActionCode + ":" + a.WFUserID + ":" + a.WFActionType + ":" + a.WFActionNextPartCode + ":" + a.WFActionNextStepCode);
};

/** Base ID/Text Description */
interface IKWFTextElement {
    /** Unique Identifier */
    ID: string;
    /** Title for Display */
    Title: string;
}

/** Client Side 'enriched' Runtime- Attached Forwarding Comment (KWF3) */
export interface IKWFComment extends IFDRW_WF_Action_Fwd_Comments_List, Partial<IKWFTextElement> { }

/** Description of an Available Routing Action (Caution : might be a VIRTUAL grouping object!) */
export interface IKWFRoutingAction extends IKWFTextElement, IFDRW_Workflow_Instance_Action_List {
    /** for a Virtual Group Item : the Container of Related Items, undefined for real items */
    _Items?: IKWFRoutingAction[];
    /** for a Grouped Item: Reference to the Virtual Group Item, far a virtual Item : undefined */
    _GroupItem?: IKWFRoutingAction;
    /** for a Grouped Item : Title if displayed in group context */
    _GroupedTitle?: string;
    /** Marker : Action is considered a 'back' Action (Attached on Client) */
    _IsBackAction?: boolean;
}

/** Model for Routing Actions for a Workflow Instance */
export interface IKWFRoutingActions {
    /** Root Items for selection (might be 'real' Items if not attached to group, or Group Items (iif _Items is Set) */
    RootItems: IKWFRoutingAction[];

    /** All 'Real' Selectable Items **/
    Items: IKWFRoutingAction[];

    /** there are no groups, all Items are RootItems i.e. Items == RootItems */
    NoGroups: boolean;
}

/** Info about changed state when Routing Action Changed */
export interface IKWFRoutingInfo {
    /** Current Forwarding Comments */
    ForwardingComments: IKWFComment[];

    /** Mandatory Actions Info */
    MandatoryActions: IFDRW_Workflow_Instance_Action_List;
}
/** State Information for a Mass Route Item */
export interface IRWFRoutingState {
    /** Instance can currently not be edited (IsRouting, IsRouted) */
    Disabled?: boolean;
    /** Routing is in Progress */
    IsRouting?: boolean;
    /** Instance is already Successfully Routed */
    IsDone?: boolean;
    /** Instance is Open */
    IsOpen?: boolean;
    /** Instance currenyl gets refreshed due to external change notification */
    IsRefresh?: boolean;
    /** Is In Final Error State (must not be retried) */
    IsError?: boolean;
    /** Current Error Description */
    Error?: string | any;
    /** Marker : Upload of Single SignedDoc Failed ( Current Problems with Intarsys in Mass- Sign - Szenario) */
    SignDocError?: boolean;
}

/** Action Types Considered to be 'back' Actions */
const ActionTypesBack = [
    SelectedActionType.Back_in_workflow,
    SelectedActionType.Back_in_workflow_history,
    SelectedActionType.Back_in_workflow_with_user_selection,
    SelectedActionType.Back_to_workflow_part,
    SelectedActionType.Cancel,
    SelectedActionType.Reset,
    SelectedActionType.Back_in_workflow_history_to_step,
    SelectedActionType.Back_to_step_with_user_selection,
];

/** Action Types that might be grouped */
const ActionTypesGroupable = [
    SelectedActionType.Back_in_workflow_with_user_selection,
    SelectedActionType.Back_to_step_with_user_selection,
    SelectedActionType.Forward_to_step_with_user_selection,
    SelectedActionType.Forward_to_workflow_part
];
